import { CopyIcon, UpdateIcon } from '@radix-ui/react-icons';
import { Box, Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { t } from 'i18next';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  IBallMovement,
  IBallState,
  IClosedLoopBuildChars,
  IPitch,
  ITrajectory,
  ITrajektRefBreak,
} from 'lib_ts/interfaces/pitches';
import { clone, first } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { PitchListsService } from 'services/pitch-lists.service';
import { PitchesService } from 'services/pitches.service';
import { StateTransformService } from 'services/state-transform.service';
import useListLibraryStore from 'stores/use-list-library-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const COMPONENT_NAME = 'OptimizePitchDialog';

interface IProps {
  pitch: IPitch;

  onClose: () => void;

  // hides the update button
  readonly: boolean;
}

export const OptimizePitchDialog = (props: IProps) => {
  const activeList = useListLibraryStore((s) => s.activeList);

  const trainingShots = useTrainedMsStore(
    (s) => s.trainingShotsDict[props.pitch.bs_hash] ?? []
  );

  // load training shots
  useEffect(() => {
    useTrainedMsStore.getState().lazyLoadShots(props.pitch.bs_hash, false);
  }, [props.pitch.bs_hash]);

  const medians = useMemo(() => {
    if (trainingShots.length === 0) {
      return undefined;
    }

    const bss = trainingShots
      .filter((s) => !!s.bs)
      .map((s) => s.bs as IBallState);
    const breaks = trainingShots.map((s) => TrajHelper.getBreaksFromShot(s));
    const trajs = trainingShots
      .filter((s) => !!s.traj)
      .map((s) => s.traj as ITrajectory);

    return {
      bs:
        bss.length > 0
          ? (MiscHelper.getMedianObject(bss) as IBallState)
          : undefined,
      break:
        breaks.length > 0
          ? (MiscHelper.getMedianObject(breaks) as ITrajektRefBreak)
          : undefined,
      traj:
        trajs.length > 0
          ? (MiscHelper.getMedianObject(trajs) as ITrajectory)
          : undefined,
    };
  }, [trainingShots]);

  const submit = useCallback(
    async (mode: 'copy' | 'update') => {
      if (mode === 'update' && props.readonly) {
        NotifyHelper.error({
          message_md: 'Updating is not allowed in read-only mode.',
        });
        return;
      }

      if (props.pitch.priority === 'Spins' && !medians?.bs) {
        NotifyHelper.error({
          message_md: 'Optimize pitch with spin priority requires median BS.',
        });
        return;
      }

      // in trajekt frame of ref
      const pitch_breaks =
        props.pitch.breaks ?? TrajHelper.getBreaks(props.pitch.traj);

      if (
        props.pitch.priority === 'Breaks' &&
        (!medians?.break || !pitch_breaks)
      ) {
        NotifyHelper.error({
          message_md:
            'Optimize pitch with break priority requires median breaks.',
        });
        return;
      }

      if (!medians?.traj) {
        NotifyHelper.error({
          message_md: 'There is no median trajectory.',
        });
        return;
      }

      const tms = useTrainedMsStore.getState().getTms(props.pitch.bs_hash);

      if (!tms?.ms) {
        NotifyHelper.error({ message_md: 'There is no machine state.' });
        return;
      }

      const target_mvmt = pitch_breaks
        ? ({
            break_x_ft: pitch_breaks.xInches / FT_TO_INCHES,
            break_z_ft: pitch_breaks.zInches / FT_TO_INCHES,
          } as IBallMovement)
        : undefined;

      const actual_mvmt = medians?.break
        ? ({
            break_x_ft: medians?.break.xInches / FT_TO_INCHES,
            break_z_ft: medians?.break.zInches / FT_TO_INCHES,
          } as IBallMovement)
        : undefined;

      const chars: IClosedLoopBuildChars = {
        mongo_id: props.pitch._id,
        ms: tms.ms,
        traj: medians?.traj,
        seams: props.pitch.seams,
        target_bs: props.pitch.bs,
        actual_bs: medians.bs ?? props.pitch.bs,
        priority: props.pitch.priority,
        target_mvmt: target_mvmt,
        actual_mvmt: actual_mvmt,
        use_gradient: true,
      };

      const result = first(
        await StateTransformService.getInstance().buildManyClosedLoop({
          machineID: tms.machineID,
          pitches: [chars],
          stepSize: 1,
          notifyError: true,
        })
      );

      if (!result) {
        NotifyHelper.error({
          message_md: 'Failed to find adjusted pitch by ID.',
        });
        return;
      }

      const upsertedTms = await useTrainedMsStore.getState().upsertTms({
        machineID: tms.machineID,
        ball_type: tms.ball_type,
        bs_hash: MachineHelper.getBsHash(result.target_bs, chars.priority),
        ms: result.ms,
        shotIDs: [],
        contShotErrors: null,
        aimingTraj: result.traj,
        // * IMPORTANT: prevents PL from refreshing the ms before it can be trained
        skipRefresh: true,
        deleted: false,
      });

      if (!upsertedTms) {
        NotifyHelper.error({
          message_md: 'Failed to create optimized machine state.',
        });
        return;
      }

      const payload: Partial<IPitch> = clone(props.pitch);

      payload.bs = result.target_bs;
      payload.bs_hash = upsertedTms.bs_hash;

      // if user wants to train right away, otherwise the TMS will provide the ms in future sessions
      payload._mx_tms = upsertedTms;

      payload.seams = result.seams;

      payload._original_id = props.pitch._id;

      if (mode === 'copy') {
        payload.name = props.pitch.name?.endsWith(' (OPT)')
          ? props.pitch.name
          : `${props.pitch.name} (OPT)`;
      }

      // add the new pitch the list
      await PitchListsService.getInstance()
        .addPitches({
          listID: props.pitch._parent_id,
          data: [payload],
        })
        .then((result) => {
          if (result.success) {
            return;
          }

          NotifyHelper.warning({
            message_md: result.error ?? 'Pitch could not be created.',
          });
        });

      // when updating, the new pitch has to replace the old version
      if (mode === 'update') {
        await PitchesService.getInstance().markAsDeleted([props.pitch._id]);
      }

      usePitchListStore.getState().reloadPitches({
        activeList: activeList,
      });

      props.onClose();
    },
    [props.readonly, props.pitch, props.onClose, medians, activeList]
  );

  const canSave = (() => {
    if (!medians) {
      return false;
    }

    switch (props.pitch.priority) {
      case BuildPriority.Breaks: {
        return !!props.pitch.breaks && !!medians.break && !!medians.traj;
      }

      case BuildPriority.Spins:
      case BuildPriority.Default:
      default: {
        return !!medians.bs;
      }
    }
  })();

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        width={RADIX.DIALOG.WIDTH.MD}
        title={`${t('pl.optimize-pitch')} (BETA)`}
        content={
          <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
            <Box>
              <Text>
                If a trained pitch has a different speed or spin rate than the
                uploaded target, this is the best method to approach the target.
              </Text>
            </Box>

            <CommonCallout text="Updating pitch characteristics will require retraining the pitch before it can be used." />
          </Flex>
        }
        buttons={[
          {
            icon: <CopyIcon />,
            label: 'common.copy',
            color: RADIX.COLOR.SUCCESS,
            variant: 'solid',
            disabled: !canSave,
            onClick: () => submit('copy'),
          },
          {
            invisible: props.readonly,
            icon: <UpdateIcon />,
            label: 'common.update',
            color: RADIX.COLOR.WARNING,
            disabled: !canSave,
            onClick: () => submit('update'),
          },
        ]}
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
